import { useEffect, useState, useRef } from "react";
import { RiCloseLine } from "react-icons/ri";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getTransfers, validateTransfer, validateAllTransferItems } from "UpdatedFeatures/api";
import { CiCircleCheck } from "react-icons/ci";
import { toast } from "react-toastify";
import { Puff } from "react-loader-spinner";

const TransferValidationModal = ({ open, setOpen, selectedTransfer }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [transferStatus, setTransferStatus] = useState(null);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const queryClient = useQueryClient();
  const [loadingStates, setLoadingStates] = useState({});
  const [isValidatingAll, setIsValidatingAll] = useState(false);
  const currentValidatingItemRef = useRef(null); // Reference to track the current item being validated

  console.log(selectedTransfer)

  // Fetch the latest transfer data
  const { data, isLoading: isTransferLoading } = useQuery({
    queryKey: ['transfer-details', selectedTransfer?.id],
    queryFn: () => getTransfers(1, 10, '', false, null, null, selectedTransfer?.id),
    enabled: !!selectedTransfer?.id && open,
  });
  console.log(selectedTransfer)
  const [transfer, setTransfer] = useState(null);

  useEffect(() => {
    if (selectedTransfer) {
      setTransferStatus(selectedTransfer.status);
      setTransfer(selectedTransfer);
    }
  }, [selectedTransfer]);

  useEffect(() => {
    if (data?.data?.length > 0) {
      const transferData = data.data.find(item => item.id === selectedTransfer?.id);
      if (transferData) {
        setTransfer(transferData);
      }
    }
  }, [data, selectedTransfer]);

  // Initialize received quantities
  useEffect(() => {
    if (selectedTransfer?.transfert_receivers) {
      const initialQuantities = {};
      selectedTransfer.transfert_receivers.forEach(receiver => {
        const key = `${receiver.receiver_pos_id}-${receiver.product_id}`;
        initialQuantities[key] = receiver.received_quantity ? receiver.received_quantity : receiver.quantity;
      });
      setReceivedQuantities(initialQuantities);
    }
  }, [selectedTransfer]);

  // Mutation to validate a single product transfer
  const validateItemMutation = useMutation({
    mutationFn: validateTransfer,
    onSuccess: () => {
      queryClient.invalidateQueries(['transfers']);
      queryClient.invalidateQueries(['transfer-details', selectedTransfer?.id]);
      toast.success("Produit transféré avec succès");
      setOpen(false)
      // Use the reference to clear the loading state
      if (currentValidatingItemRef.current) {
        setLoadingStates(prev => ({
          ...prev,
          [currentValidatingItemRef.current]: false
        }));
        currentValidatingItemRef.current = null;
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la validation: ${error.message}`);

      // Use the reference to clear the loading state
      if (currentValidatingItemRef.current) {
        setLoadingStates(prev => ({
          ...prev,
          [currentValidatingItemRef.current]: false
        }));
        currentValidatingItemRef.current = null;
      }
    },
  });

  // Mutation to validate all products in the transfer
  const validateAllMutation = useMutation({
    mutationFn: validateAllTransferItems,
    onSuccess: () => {
      queryClient.invalidateQueries(['transfers']);
      queryClient.invalidateQueries(['transfer-details', selectedTransfer?.id]);
      toast.success("Transfert validé avec succès");
      setTransferStatus(2);
      setIsValidatingAll(false);
      setOpen(false);
    },
    onError: (error) => {
      toast.error(`Erreur lors de la validation: ${error.message}`);
      setIsValidatingAll(false);
    },
  });

  const handleQuantityChange = (receiverPosId, productId, value) => {
    const key = `${receiverPosId}-${productId}`;
    setReceivedQuantities({
      ...receivedQuantities,
      [key]: parseInt(value) || 0,
    });
  };

  const handleValidateItem = (receiverPosId, productId) => {
    const key = `${receiverPosId}-${productId}`;

    // Set current validating item
    currentValidatingItemRef.current = key;

    // Update loading state
    setLoadingStates(prev => ({
      ...prev,
      [key]: true
    }));

    validateItemMutation.mutate({
      transferId: selectedTransfer.id,
      receiverPosId,
      productId,
      quantity: receivedQuantities[key],
    });
  };

  const handleValidateAll = () => {
    setIsValidatingAll(true);
    validateAllMutation.mutate({
      transferId: selectedTransfer.id,
      receivedQuantities,
    });
  };

  // Check if a product is already validated
  const isProductValidated = (receiverId) => {
    const receiver = selectedTransfer?.transfert_receivers?.find(r => r.id === receiverId);
    return receiver?.status === 1;
  };

  // Group receivers by POS
  const groupReceiversByPos = () => {
    if (!selectedTransfer?.transfert_receivers) return [];
    console.log(selectedTransfer?.transfert_receivers)
    const posGroups = {};

    selectedTransfer.transfert_receivers.forEach(receiver => {
      const posId = receiver.receiver_pos_id;

      if (!posGroups[posId]) {
        posGroups[posId] = {
          receiver_pos_id: posId,
          receiver_pos: receiver.receiver_pos,
          receiver_pos_name: receiver.receiver_pos_name,
          items: []
        };
      }

      posGroups[posId].items.push(receiver);
    });

    return Object.values(posGroups);
  };

  if (!open) return null;

  const posGroups = groupReceiversByPos();
  console.log(posGroups)
  return (
    <div
      className="fixed inset-0 z-50"
      style={{ zIndex: 9991, overflow: "auto" }}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />

      {/* Modal Content */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="relative w-full max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 bg-[#b269ae] border-b border-gray-500/30">
            <h3 id="modal-title" className="text-lg font-medium leading-6 text-white">
              Valider le Transfert
            </h3>
            <button
              type="button"
              className="inline-flex items-center justify-center p-1.5 text-white hover:text-gray-200"
              onClick={() => setOpen(false)}
            >
              <RiCloseLine className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Transfer Info */}
          <div className="p-4 border-b mx-5 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Référence: <span className="font-semibold text-[#b269ae]">{`TRF${selectedTransfer?.id}`}</span></p>
              <p className="text-sm text-gray-600">Provenance: <span className="font-semibold text-[#b269ae]">{selectedTransfer.sender_pos == null
                ? selectedTransfer.sender_pos_name || "KD Marché"
                : selectedTransfer.sender_pos.name}</span></p>
            </div>
            <button
              onClick={handleValidateAll}
              className="block rounded-md bg-yellow-300 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-yellow-400 duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 disabled:bg-gray-700/40 disabled:cursor-not-allowed"
              disabled={transferStatus === 1 || isValidatingAll}
            >
              {isValidatingAll ? (
                <span className="flex items-center">
                  <Puff color="white" height={16} width={16} className="mr-2" />
                  Validation...
                </span>
              ) : (
                "Valider Tout"
              )}
            </button>
          </div>

          {isTransferLoading ? (
            <div className="flex justify-center items-center p-8">
              <Puff color="#b269ae" height={40} width={40} />
            </div>
          ) : (
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {posGroups.map((posGroup) => (
                <div key={posGroup.receiver_pos_id} className="mb-8">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b">
                    Destination: {posGroup.receiver_pos == null && posGroup.receiver_pos_name == null ? "KD Marché" : posGroup.receiver_pos_name}
                  </h4>

                  <div className="space-y-6">
                    {posGroup.items.map((item) => {
                      const isValidated = isProductValidated(item.id);
                      const key = `${item.receiver_pos_id}-${item.product_id}`;
                      const isItemLoading = loadingStates[key];

                      return (
                        <div key={item.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="font-medium text-gray-700">Produit ID: {item.product_id}</p>
                            </div>
                            <div>
                              <p className="font-medium text-gray-700">Nom du Produit : {item.product_title}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Quantité demandée: <span className="font-semibold">{item.quantity}</span></p>
                            </div>
                          </div>

                          <div className="flex items-end gap-4">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantité reçue
                              </label>
                              <input
                                type="number"
                                min="0"
                                max={item.quantity}
                                value={receivedQuantities[key] || 0}
                                onChange={(e) => handleQuantityChange(item.receiver_pos_id, item.product_id, e.target.value)}
                                disabled={isValidated}
                                className={`w-full p-2 border rounded-md ${isValidated ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-[#b269ae] focus:border-[#b269ae]'}`}
                              />
                            </div>

                            {isValidated ? (
                              <button
                                type="button"
                                className="rounded-md bg-green-600/10 px-3 py-2 text-center text-sm font-semibold border border-green-600 text-green-600 shadow-sm duration-300 flex items-center gap-2 cursor-not-allowed"
                              >
                                <CiCircleCheck size={20} /> Validé
                              </button>
                            ) : (
                              <button
                                onClick={() => handleValidateItem(item.receiver_pos_id, item.product_id)}
                                disabled={isItemLoading || isValidatingAll}
                                className="block rounded-md bg-[#b269ae] px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-[#a058a0] duration-300 focus-visible:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed"
                              >
                                {isItemLoading ? (
                                  <span className="flex items-center">
                                    <Puff color="white" height={16} width={16} className="mr-2" />
                                    Validation...
                                  </span>
                                ) : (
                                  "Valider"
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex w-auto justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferValidationModal;